import type { BudgetSettings, CategoryRef, ReadModel } from '@tahi/core';
import { categories } from '@tahi/db';
import { and, eq, isNull } from 'drizzle-orm';
import type { LocalDb } from './localDb';
import { loadQuitReads } from './readModel.quit';
import { getBudgetSettingsView, listAccounts, listFunds } from './repos/budget';

/**
 * Loads everything core rules may read, so ReadModel can answer synchronously (conventions §15).
 * Module plans add the reads their rules need here.
 */
export async function loadReadModel(db: LocalDb, userId: string): Promise<ReadModel> {
  const cats = await db
    .select()
    .from(categories)
    .where(and(eq(categories.userId, userId), isNull(categories.deletedAt)))
    .all();
  const byKey = new Map<string, CategoryRef>();
  for (const c of cats) {
    if (c.key) byKey.set(c.key, { id: c.id, key: c.key, name: c.name, hidden: c.hidden });
  }
  // v0.2: the user's budget settings (no row = spec defaults; a deleted default fund reads as null).
  const view = await getBudgetSettingsView(db, userId);
  const budget: BudgetSettings = {
    autoDeduct: view.autoDeduct,
    defaultFundId: view.defaultFundId,
    useFunds: view.useFunds,
    defaultAccountId: view.defaultAccountId,
    incomeAccountId: view.incomeAccountId,
  };
  // v0.5: live funds with their accounts (an expense's default "From").
  const fundById = new Map(
    (await listFunds(db, userId)).map((f) => [f.id, { id: f.id, accountIds: f.accountIds }]),
  );
  // v0.6: live accounts, so a default "From" never lands in a savings-only one.
  const accounts = (await listAccounts(db, userId)).map((a) => ({
    id: a.id,
    deletedAt: a.deletedAt,
    useForSpending: a.useForSpending,
  }));
  // v0.3: the quit plan and the per-day Cigarettes rows.
  const quit = await loadQuitReads(db, userId);
  return {
    getBudgetSettings: () => ({ ...budget }),
    getDefaultFundId: () => budget.defaultFundId,
    getCategoryByKey: (key: string) => byKey.get(key) ?? null,
    getFund: (id: string) => fundById.get(id) ?? null,
    getAccounts: () => accounts,
    ...quit,
  };
}
